const HEX6 = /^#([0-9a-f]{6})$/i;
const HEX8 = /^#([0-9a-f]{8})$/i;
const HEX3 = /^#([0-9a-f]{3})$/i;
const RGBA =
  /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i;

export const DEFAULT_SOLID_FILL = { mode: 'solid', color: '#ffffff', alpha: 1 };

export const DEFAULT_GRADIENT_FILL = {
  mode: 'gradient',
  angle: 135,
  stops: [
    { color: '#2563eb', alpha: 1, position: 0 },
    { color: '#7c3aed', alpha: 1, position: 100 },
  ],
};

function expandHex3(hex) {
  const m = hex.match(HEX3);
  if (!m) return hex;
  const [, h] = m;
  return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
}

function hexToRgb(hex) {
  const h = expandHex3(String(hex || '').trim());
  const m6 = h.match(HEX6);
  if (m6) {
    const n = m6[1];
    return {
      r: parseInt(n.slice(0, 2), 16),
      g: parseInt(n.slice(2, 4), 16),
      b: parseInt(n.slice(4, 6), 16),
    };
  }
  const m8 = h.match(HEX8);
  if (m8) {
    const n = m8[1];
    return {
      r: parseInt(n.slice(0, 2), 16),
      g: parseInt(n.slice(2, 4), 16),
      b: parseInt(n.slice(4, 6), 16),
      a: parseInt(n.slice(6, 8), 16) / 255,
    };
  }
  return null;
}

export function clampAlpha(alpha) {
  const n = Number(alpha);
  if (Number.isNaN(n)) return 1;
  return Math.max(0, Math.min(1, n));
}

export function toRgba(color, alpha = 1) {
  const a = clampAlpha(alpha);
  const rgb = hexToRgb(color);
  if (rgb) {
    const finalA = rgb.a != null ? rgb.a * a : a;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${finalA})`;
  }
  const m = String(color || '').match(RGBA);
  if (m) {
    const r = Number(m[1]);
    const g = Number(m[2]);
    const b = Number(m[3]);
    const baseA = m[4] != null ? Number(m[4]) : 1;
    return `rgba(${r}, ${g}, ${b}, ${baseA * a})`;
  }
  return color || 'transparent';
}

export function hexForPicker(raw, fallback = '#ffffff') {
  const rgb = hexToRgb(raw);
  if (rgb) {
    const r = rgb.r.toString(16).padStart(2, '0');
    const g = rgb.g.toString(16).padStart(2, '0');
    const b = rgb.b.toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return fallback;
}

export function parseColorString(raw, fallback = '#ffffff') {
  if (!raw) return { ...DEFAULT_SOLID_FILL, color: hexForPicker(fallback) };
  const s = String(raw).trim();
  if (!s) return { ...DEFAULT_SOLID_FILL, color: hexForPicker(fallback) };

  const m8 = s.match(HEX8);
  if (m8) {
    const n = m8[1];
    return {
      mode: 'solid',
      color: `#${n.slice(0, 6)}`.toLowerCase(),
      alpha: parseInt(n.slice(6, 8), 16) / 255,
    };
  }

  const m6 = s.match(HEX6);
  if (m6) return { mode: 'solid', color: s.toLowerCase(), alpha: 1 };

  const m3 = s.match(HEX3);
  if (m3) return { mode: 'solid', color: expandHex3(s), alpha: 1 };

  const rgba = s.match(RGBA);
  if (rgba) {
    const r = Math.round(Number(rgba[1]));
    const g = Math.round(Number(rgba[2]));
    const b = Math.round(Number(rgba[3]));
    const a = rgba[4] != null ? Number(rgba[4]) : 1;
    const hex = `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
    return { mode: 'solid', color: hex, alpha: clampAlpha(a) };
  }

  return { ...DEFAULT_SOLID_FILL, color: hexForPicker(fallback) };
}

export function normalizeGradientStops(stops, fallback = DEFAULT_GRADIENT_FILL.stops) {
  const list = (stops?.length >= 2 ? stops : fallback).map((s, i, arr) => ({
    id: s.id || `stop-${i}-${s.position ?? 0}`,
    color: hexForPicker(s.color, '#2563eb'),
    alpha: clampAlpha(s.alpha ?? 1),
    position:
      s.position != null
        ? Math.max(0, Math.min(100, Number(s.position)))
        : arr.length <= 1
          ? 0
          : Math.round((i / (arr.length - 1)) * 100),
  }));
  return list.sort((a, b) => a.position - b.position);
}

export function suggestGradientStopPosition(stops) {
  const sorted = normalizeGradientStops(stops);
  if (sorted.length < 2) return { position: 50, color: '#ffffff' };

  let bestGap = -1;
  let position = 50;
  let color = sorted[0].color;

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const gap = sorted[i + 1].position - sorted[i].position;
    if (gap > bestGap) {
      bestGap = gap;
      position = Math.round((sorted[i].position + sorted[i + 1].position) / 2);
      color = sorted[i].color;
    }
  }

  return { position, color: hexForPicker(color, '#ffffff') };
}

export function normalizeColorFill(raw, fallback = '#ffffff') {
  if (raw == null || raw === '') {
    return parseColorString(fallback, fallback);
  }
  if (typeof raw === 'string') {
    return parseColorString(raw, fallback);
  }
  if (raw.mode === 'gradient') {
    const stops = normalizeGradientStops(raw.stops, DEFAULT_GRADIENT_FILL.stops);
    return {
      mode: 'gradient',
      angle: Number(raw.angle) || 0,
      stops,
    };
  }
  return {
    mode: 'solid',
    color: hexForPicker(raw.color, fallback),
    alpha: clampAlpha(raw.alpha ?? 1),
  };
}

export function resolveColorFillCss(fill, fallback = '#ffffff') {
  const f = normalizeColorFill(fill, fallback);
  if (f.mode === 'gradient') {
    const stops = f.stops
      .map((s) => `${toRgba(s.color, s.alpha ?? 1)} ${s.position}%`)
      .join(', ');
    return `linear-gradient(${f.angle ?? 180}deg, ${stops})`;
  }
  return toRgba(f.color, f.alpha ?? 1);
}

export function resolveColorFillStyle(fill, fallback = '#ffffff') {
  const f = normalizeColorFill(fill, fallback);
  if (f.mode === 'gradient') {
    return {
      backgroundColor: 'transparent',
      backgroundImage: resolveColorFillCss(f, fallback),
    };
  }
  return { backgroundColor: resolveColorFillCss(f, fallback) };
}

/** Style pour texte (couleur unie ou dégradé). */
export function resolveTextFillStyle(fill, fallback = '#111827') {
  const f = normalizeColorFill(fill, fallback);
  if (f.mode === 'gradient') {
    return {
      backgroundImage: resolveColorFillCss(f, fallback),
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
    };
  }
  return { color: resolveColorFillCss(f, fallback) };
}

/** Couleur unie pour traits SVG (icônes) — dégradé → première stop. */
export function resolveIconStrokeColor(fill, fallback = '#2563eb') {
  const f = normalizeColorFill(fill, fallback);
  if (f.mode === 'gradient') {
    const first = f.stops[0];
    return toRgba(first?.color || fallback, first?.alpha ?? 1);
  }
  return toRgba(f.color, f.alpha ?? 1);
}

export function isGradientFill(fill) {
  return normalizeColorFill(fill).mode === 'gradient';
}

export function resolveShapeRadius(shape, borderRadius) {
  switch (shape) {
    case 'square':
      return 0;
    case 'rounded':
      return Math.max(0, Number(borderRadius) ?? 12);
    case 'pill':
      return 9999;
    case 'circle':
      return 9999;
    case 'custom':
      return Math.max(0, Number(borderRadius) || 0);
    default:
      return Math.max(0, Number(borderRadius) ?? 8);
  }
}
